/**
 * crmFunnel/leadHistory/useLeadHistoryAnalytics.ts
 *
 * Fetches pre-computed analytics from /api/buying-cycle/analytics.
 * Server does the join + calculations — browser just renders charts.
 */

import { useState, useEffect, useRef } from "react";
import type { LeadHistoryAnalytics } from "./types";

export interface BuyingCycleFilters {
  startDate?: string;
  endDate?: string;
  source?: string;
  funnel?: string;
  utmContent?: string;
  leadType?: string;
  responseType?: string;
  country?: string;
  closer?: string;
  setter?: string;
}

export interface LeadHistoryAnalyticsResult {
  analytics: LeadHistoryAnalytics | null;
  loading: boolean;
  error: string | null;
  total: number;
}

export function useLeadHistoryAnalytics(
  filters: BuyingCycleFilters = {},
): LeadHistoryAnalyticsResult {
  const [analytics, setAnalytics] = useState<LeadHistoryAnalytics | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [total,     setTotal]     = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const { startDate, endDate, source, funnel, utmContent, leadType, responseType, country, closer, setter } = filters;

  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (startDate)    params.set("startDate",    startDate);
    if (endDate)      params.set("endDate",      endDate);
    if (source)       params.set("source",       source);
    if (funnel)       params.set("funnel",       funnel);
    if (utmContent)   params.set("utmContent",   utmContent);
    if (leadType)     params.set("leadType",     leadType);
    if (responseType) params.set("responseType", responseType);
    if (country)      params.set("country",      country);
    if (closer)       params.set("closer",       closer);
    if (setter)       params.set("setter",       setter);

    const url = `/api/buying-cycle/analytics${params.toString() ? `?${params}` : ""}`;

    fetch(url, { signal: ctrl.signal })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ analytics: LeadHistoryAnalytics; total: number }>;
      })
      .then(data => {
        setAnalytics(data.analytics);
        setTotal(data.total ?? 0);
        setLoading(false);
      })
      .catch(err => {
        if ((err as Error).name === "AbortError") return;
        setError(String(err));
        setLoading(false);
      });

    return () => ctrl.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, source, funnel, utmContent, leadType, responseType, country, closer, setter]);

  return { analytics, loading, error, total };
}
